import React, { useState, useRef, useEffect } from 'react';
import { FiUser, FiLogOut, FiChevronDown } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

/**
 * ProfileHeader - Top-right user profile dropdown
 * Shows avatar (initials), name, email, and dropdown menu
 */
const ProfileHeader = () => {
  const { user, userEmail, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get initials from name or email
  const getInitials = () => {
    if (user?.full_name) {
      return user.full_name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (userEmail) {
      return userEmail.slice(0, 2).toUpperCase();
    }
    return 'U';
  };

  const displayName = user?.full_name || userEmail?.split('@')[0] || 'User';
  const displayEmail = userEmail || '';

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Profile Button */}
      <button
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className="flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        {/* Avatar Circle */}
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-base shadow-md">
          {getInitials()}
        </div>
        
        {/* Name & Email (hidden on small screens) */}
        <div className="hidden md:block text-left">
          <p className="text-base font-semibold text-black leading-tight">
            {displayName}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-tight">
            {displayEmail}
          </p>
        </div>

        {/* Dropdown Icon */}
        <FiChevronDown 
          className={`text-gray-500 dark:text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      {dropdownOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50">
          {/* User Info Section */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <p className="text-base font-semibold text-gray-900 dark:text-white">
              {displayName}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {displayEmail}
            </p>
            {user?.role && (
              <span className="inline-block mt-2 px-2 py-1 text-sm font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                {user.role === 'factory_admin' ? 'Admin' : 'Staff'}
              </span>
            )}
          </div>

          {/* Menu Items */}
          <div className="py-2">
            <button
              onClick={() => {
                /* Add profile page navigation if needed */
                setDropdownOpen(false);
              }}
              className="w-full px-4 py-2.5 text-left text-base text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
            >
              <FiUser className="text-gray-500" />
              Profile
            </button>

            <button
              onClick={() => {
                setDropdownOpen(false);
                logout();
              }}
              className="w-full px-4 py-2.5 text-left text-base text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
            >
              <FiLogOut className="text-red-500" />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileHeader;
